import _toConsumableArray from '@babel/runtime/helpers/toConsumableArray';
import _defineProperty from '@babel/runtime/helpers/defineProperty';
import { aggLogic } from './aggUtils.js';

function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }

/**
 * updateColumnsDef - will update all columns
 * @param {object} props
 * @param {Array<{field: string}>} props.columnDefs column definitions
 * @param {string} props.field will identify which column to update by field
 * @param {('asc' | 'dsc'| null)} props.sortDirection the sort direction
 * @param {'numeric' | 'boolean' | 'date' | 'character' | null} props.sortType the type of sort
 * @param {number} props.width the width of the column
 * @param {any} defaultColDef the default column definition before it is overwritten
 * @returns {Array<{field: string}>} an updated list of Column Defs
 */

var updateColumnsDef = function updateColumnsDef(_ref, defaultColDef) {
  var columnDefs = _ref.columnDefs,
    field = _ref.field,
    sortDirection = _ref.sortDirection,
    sortType = _ref.sortType,
    width = _ref.width;
  return columnDefs.map(function (columnDef, index) {
    var isColumnMatch = columnDef.field === field;
    return _objectSpread(_objectSpread(_objectSpread({}, columnDef), defaultColDef), {}, {
      index: index,
      shouldSort: isColumnMatch,
      sortDirection: isColumnMatch ? sortDirection : columnDef.sortDirection,
      sortField: isColumnMatch ? field : columnDef.field,
      sortType: isColumnMatch ? sortType : columnDef.sortType,
      width: isColumnMatch && width || columnDef.width || 250
    });
  });
};

/**
 * getLastColumnWithDefaultSort - when there are more than one defaultSort from column in column definitions, use the last default sort
 * @param {Array<{ field: string}>} columnDefs
 * @returns {{field: string}}
 */
var getLastColumnWithDefaultSort = function getLastColumnWithDefaultSort(columnDefs) {
  var columnsWithDefaultSort = columnDefs === null || columnDefs === void 0 ? void 0 : columnDefs.filter(function (def) {
    return def.defaultSort;
  });
  return Array.isArray(columnsWithDefaultSort) ? columnsWithDefaultSort[columnsWithDefaultSort.length - 1] : {};
};

/**
 * getSortDirection will return the sort direction in respects to overwrite priority
 * @param { 'asc' | 'dsc' | null } payloadSortDirection sort direction from a new payload
 * @param { 'asc' | 'dsc' | null } stateSortDirection sort direction from the local state
 * @param { 'asc' | 'dsc' | null } defaultSortDirection sort direction from the initial default state
 * @returns { 'asc' | 'dsc' | null }
 */
var getSortDirection = function getSortDirection(payloadSortDirection, stateSortDirection, defaultSortDirection) {
  var sortTypes = ['asc', 'dsc', null];
  if (sortTypes.includes(payloadSortDirection)) {
    return payloadSortDirection;
  }
  if (sortTypes.includes(stateSortDirection)) {
    return stateSortDirection;
  }
  if (sortTypes.includes(defaultSortDirection)) {
    return defaultSortDirection;
  }
};

/**
 * mapColumnsDef - when new columns data is set by the reducer, this function ensures that columns are returned in the correct overwrite order
 * @param {any} payloadColumnDefs
 * @param {any} stateColumnDefs
 * @param {any} defaultColDef
 * @returns {Array<any>}
 */
var mapColumnsDef = function mapColumnsDef(payloadColumnDefs, stateColumnDefs, defaultColDef, rowHeaderField) {
  var lastColumnWithDefaultSort = getLastColumnWithDefaultSort(payloadColumnDefs);
  return (payloadColumnDefs === null || payloadColumnDefs === void 0 ? void 0 : payloadColumnDefs.map(function (payloadDef, index) {
    var hasColumnWithDefaultSort = (lastColumnWithDefaultSort === null || lastColumnWithDefaultSort === void 0 ? void 0 : lastColumnWithDefaultSort.field) === payloadDef.field;
    var foundStateDef = stateColumnDefs === null || stateColumnDefs === void 0 ? void 0 : stateColumnDefs.find(function (stateDef) {
      return stateDef.field === payloadDef.field;
    });
    var sortDirectionToUse = getSortDirection(payloadDef === null || payloadDef === void 0 ? void 0 : payloadDef.sortDirection, foundStateDef === null || foundStateDef === void 0 ? void 0 : foundStateDef.sortDirection, payloadDef === null || payloadDef === void 0 ? void 0 : payloadDef.defaultSort);
    var isRowHeader = payloadDef.field === rowHeaderField;
    return _objectSpread(_objectSpread(_objectSpread(_objectSpread({}, defaultColDef), {}, {
      shouldSort: hasColumnWithDefaultSort
    }, foundStateDef), payloadDef), {}, {
      sortDirection: sortDirectionToUse,
      // eslint-disable-next-line sort-keys
      index: index,
      isRowHeader: isRowHeader,
      width: payloadDef.width || (defaultColDef === null || defaultColDef === void 0 ? void 0 : defaultColDef.width) || 250
    });
  })) || [];
};

/**
 * getHasRowGroup - determines if there is a row grouping within column definitions
 * @param {any} columnDefs
 * @returns {any}
 */
var getHasRowGroup = function getHasRowGroup(columnDefs) {
  return !!(columnDefs !== null && columnDefs !== void 0 && columnDefs.find(function (def) {
    return def.rowGroup;
  }));
};

/**
 * getDisplayColumns - will show the column header names based on row grouping
 * @param {any} columnDefs
 * @param {'multipleColumns' | 'singleColumn' | undefined} groupDisplayType
 * @returns {any}
 */
var getDisplayColumns = function getDisplayColumns(columnDefs, groupDisplayType) {
  var hasSingleRowGroup = getHasRowGroup(columnDefs) && groupDisplayType !== 'multipleColumns';
  return hasSingleRowGroup ? columnDefs === null || columnDefs === void 0 ? void 0 : columnDefs.reduce(function (acc, curr) {
    if (!curr.rowGroup || curr.isRowHeader) {
      return [].concat(_toConsumableArray(acc), [curr]);
    }
    return acc;
  }, []) : columnDefs || [];
};
var getHasStringWithSpace = function getHasStringWithSpace(columnDefs) {
  return columnDefs.map(function (item) {
    return item.field.indexOf(' ');
  }).some(function (spaceIndex) {
    return spaceIndex !== -1;
  });
};

/**
 * doColumnFieldWarning - will log errors based on column checks
 */
var doColumnFieldWarning = function doColumnFieldWarning(props) {
  var columns = props.columns;
    props.rowSelection;
    var sortableColumns = props.sortableColumns;

  // check for spaces in strings
  var hasStringWithSpace = getHasStringWithSpace(columns.columnDefs);

  //check for duplicate field names
  var columnSet = new Set(columns.columnDefs);
  if (columnSet.size !== columns.columnDefs.length || hasStringWithSpace) {
    console.error('Hey Canvas User, Fields in this data structure must be unique and have no spaces!');
  }
  var hasRowGroup = getHasRowGroup(columns.columnDefs);
  if (hasRowGroup & sortableColumns) {
    console.error('Hey Canvas User, Row Groups & Sortable Columns will be supported in the near future!');
  }
};

/**
 * getColumnRowHeader - returns the column row header
 * @param {any} columnDefs
 * @returns {any}
 */
var getColumnRowHeader = function getColumnRowHeader(columnDefs) {
  //get the column that will act as each row's header for A11y,
  //error if one is not found or if multiples are found
  var columnRowHeader = columnDefs === null || columnDefs === void 0 ? void 0 : columnDefs.filter(function (column) {
    return column.isRowHeader === true;
  });
  if ((columnRowHeader === null || columnRowHeader === void 0 ? void 0 : columnRowHeader.length) < 1) {
    console.error("Hey Canvas User! Please add the prop 'isRowHeader' to one of your columns. This will set that column's data as the header for each row and will allow screen readers to read it out. Good candidates for row headers are data items that tell the user something about the entire row: email, store id, or product name may be good options.");
    return columnDefs[0]; // set row header to first column in order to prevent erroring
  } else if ((columnRowHeader === null || columnRowHeader === void 0 ? void 0 : columnRowHeader.length) > 1) {
    console.error("Hey Canvas User! Multiple columns have the prop 'isRowHeader'. Please designate only one column as the row header column.");
    return columnDefs[0]; // set row header to first column in order to prevent erroring
  } else {
    return columnRowHeader === null || columnRowHeader === void 0 ? void 0 : columnRowHeader[0];
  }
};

/**
 * getColumnsWithAgg - returns column definitions with aggregation values
 * @param {Array<any>} aggRows array of rows in group
 * @param {Array<any>} columnDefs
 * @returns {Array<any>}
 */
var getColumnsWithAgg = function getColumnsWithAgg(aggRows) {
  var columnDefs = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : [];
  return columnDefs === null || columnDefs === void 0 ? void 0 : columnDefs.map(function (def) {
    if (def !== null && def !== void 0 && def.aggFunc) {
      var aggFunc = def.aggFunc,
        field = def.field;
      return aggLogic(aggRows, aggFunc, field);
    }
    return null;
  });
};

/**
 * setFirstColumnAsShouldSort - when there is row grouping always apply sorting to the first column to prevent results from jumping around when the consumer attempts to sort children
 * @param {Array<any>} columnDefs
 * @returns {Array<any>}
 */
var setFirstColumnAsShouldSort = function setFirstColumnAsShouldSort(columnDefs) {
  if (!Array.isArray(columnDefs)) {
    return [];
  }
  return Object.assign([], columnDefs, {
    0: _objectSpread(_objectSpread({}, columnDefs[0]), {}, {
      shouldSort: true
    })
  });
};

/**
 * getTableWidth returns the width of the table, providing a width to the table will prevent scroll from jumping when the table is rerendered
 * @param {Array<any>} columnDefs
 * @param {boolean} scrollableWidth
 * @param {'multipleColumns' | 'singleColumn' | null} groupDisplayType
 * @returns {null | string}
 */

var getTableWidth = function getTableWidth(columnDefs, scrollableWidth, groupDisplayType) {
  if (scrollableWidth) {
    var isMultiColumnRowGroup = groupDisplayType === 'multipleColumns';
    var width = columnDefs.reduce(function (acc, curr, index) {
      var isSingeColumnRowGroup = !isMultiColumnRowGroup && !curr.rowGroup;
      if (isSingeColumnRowGroup || isMultiColumnRowGroup || index === 0) {
        return acc + curr.width;
      }
      return acc;
    }, 0);
    return {
      width: "".concat(width, "px")
    };
  }
  return null;
};

export { doColumnFieldWarning, getColumnRowHeader, getColumnsWithAgg, getDisplayColumns, getHasRowGroup, getHasStringWithSpace, getLastColumnWithDefaultSort, getSortDirection, getTableWidth, mapColumnsDef, setFirstColumnAsShouldSort, updateColumnsDef };
